
import React, { createContext, useContext, useEffect, useState } from 'react';
import { cookieConsent } from '@/utils/cookieConsent';

type Theme = 'light' | 'dark';

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
const THEME_STORAGE_KEY = 'preferred-theme';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize theme from local storage or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    // Try to get the theme from localStorage first (server-side compatible check)
    if (typeof window !== 'undefined') {
      // Only use stored theme if preferences cookies are allowed
      if (cookieConsent.isCategoryAllowed('preferences')) {
        const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
        if (storedTheme) {
          return storedTheme;
        }
      }
      // If no stored preference or preferences not allowed, use system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    // Default to light mode
    return 'light';
  });

  // Update document when theme changes
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
    
    // Only save to localStorage if preferences cookies are allowed
    if (typeof window !== 'undefined' && cookieConsent.isCategoryAllowed('preferences')) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  // Listen for consent changes
  useEffect(() => {
    const handleConsentChange = (consent: any) => {
      if (!consent.preferences) {
        // Remove theme preference when preferences are disabled
        if (typeof window !== 'undefined') {
          localStorage.removeItem(THEME_STORAGE_KEY);
        }
      }
    };

    cookieConsent.addListener(handleConsentChange);

    return () => {
      cookieConsent.removeListener(handleConsentChange);
    };
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
